import React, { useEffect, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import VehiclePreviousRequests from "../../../masters/vehicles/components/VehiclePreviousRequests";

const money = (n) => Number(n || 0).toLocaleString("en-KE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * Approve or reject one submitted vehicle maintenance request.
 *
 * Approve and reject share a modal because they are the same decision taken
 * two ways over the same row, and the only difference is which field is
 * mandatory - a rejection needs a reason, an approval does not.
 *
 * The cash amount matters: `RequestedCashAmount` is what the requester ASKED
 * for and is informational only - the approver independently supplies the
 * figure that is actually authorised, and that is what the auto-created Petty
 * Cash voucher is raised for. So it is pre-filled from the request (the common
 * case is "yes, that much") but stays editable, and the requested figure is
 * shown alongside so a changed number is visible rather than silent.
 */
const VehicleRequestDecisionModal = ({ show, mode, request, onClose, onConfirm, isSaving }) => {
  const isApprove = mode === "approve";
  const [cashAmount, setCashAmount] = useState("");
  const [comment, setComment] = useState("");
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (show) {
      setCashAmount(request?.requestedCashAmount ?? "");
      setComment("");
      setReason("");
    }
  }, [show, request?.recordGuid, request?.requestedCashAmount]);

  if (!request) return null;

  // The server raises a voucher only when the requester filled in the cash
  // remarks - an amount on its own is ignored (VehiclesService.ApproveRequestAsync).
  // So the cash box is offered only then; promising a voucher for a request
  // that cannot produce one was a false message (fixed 2026-09-19).
  const hasCashAsk = Boolean(request.cashRequestRemarks && request.cashRequestRemarks.trim());

  const canConfirm = isApprove ? true : reason.trim().length > 0;

  const handleConfirm = () =>
    onConfirm(
      isApprove
        ? {
            cashAmount: !hasCashAsk || cashAmount === "" ? null : Number(cashAmount),
            approvalComment: comment.trim() || null,
          }
        : { rejectionReason: reason.trim() }
    );

  return (
    <Modal show={show} onHide={isSaving ? undefined : onClose} centered backdrop="static" size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {isApprove ? "Approve" : "Reject"} {request.requestNumber}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <dl className="row mb-3" style={{ fontSize: 13 }}>
          <dt className="col-4 text-muted fw-normal">Vehicle</dt>
          <dd className="col-8 fw-semibold mb-1">
            {request.regNo} {request.brand ? `(${request.brand} ${request.model || ""})` : ""}
          </dd>
          <dt className="col-4 text-muted fw-normal">Requested by</dt>
          <dd className="col-8 mb-1">{request.requestedByName || "—"}</dd>
          <dt className="col-4 text-muted fw-normal">Work</dt>
          <dd className="col-8 mb-1">{request.serviceRepairDescription || "—"}</dd>
          <dt className="col-4 text-muted fw-normal">Spares listed</dt>
          <dd className="col-8 mb-1">{request.spareItemCount ?? 0}</dd>
          {request.remarks && (
            <>
              <dt className="col-4 text-muted fw-normal">Remarks</dt>
              <dd className="col-8 mb-1">{request.remarks}</dd>
            </>
          )}
          <dt className="col-4 text-muted fw-normal">Cash requested</dt>
          <dd className="col-8 mb-0">
            {request.requestedCashAmount == null ? "none" : money(request.requestedCashAmount)}
            {hasCashAsk && <span className="d-block text-muted">{request.cashRequestRemarks}</span>}
          </dd>
        </dl>

        <VehiclePreviousRequests vehicleId={request.vehicleId} excludeRecordGuid={request.recordGuid} />

        {isApprove ? (
          <>
            {hasCashAsk ? (
              <div className="mb-3">
                <label className="form-label">
                  Cash to authorise <span className="text-danger">*</span>
                </label>
                <input
                  type="number"
                  min="0"
                  step="any"
                  className="form-control"
                  value={cashAmount}
                  onChange={(e) => setCashAmount(e.target.value)}
                />
                <small className="text-muted">
                  A petty cash voucher is raised for this amount, in the requester&rsquo;s name. It still has
                  to be approved and paid in Petty Cash.
                </small>
              </div>
            ) : (
              <div className="alert alert-secondary py-2 mb-3" style={{ fontSize: 13 }}>
                No petty cash voucher will be raised - the requester did not fill in the cash request
                remarks{request.requestedCashAmount != null ? ", so the amount shown is only an estimate of the spares" : ""}.
                Spares go out on an LPO.
              </div>
            )}
            <div className="mb-0">
              <label className="form-label">Comment</label>
              <textarea
                className="form-control"
                rows={2}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="optional"
              />
            </div>
          </>
        ) : (
          <div className="mb-0">
            <label className="form-label">
              Reason <span className="text-danger">*</span>
            </label>
            <textarea
              className="form-control"
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why is this being rejected? The requester sees this."
            />
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          variant={isApprove ? "success" : "danger"}
          onClick={handleConfirm}
          disabled={isSaving || !canConfirm}
        >
          {isSaving ? "Saving…" : isApprove ? "Approve" : "Reject"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default VehicleRequestDecisionModal;
